// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// polyfills
import './utils/polyfills'; // includes Array.findIndex, Array.find, etc.

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

// Set up custom time locale (as in your original code)
d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ],
  shortMonths: [
    'Ene.', 'Feb.', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Ago.', 'Sept.', 'Oct.', 'Nov.', 'Dic.',
  ],
});

// Global vars
let rawData;
let chartData;
let minDate;
let maxDate;
let minValue;
let maxValue;

const margins = {
  top: 30,
  right: 140,
  bottom: 25,
  left: 30,
};

// Initialize pym
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // In dev mode, populate meta text placeholders
  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);
      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  // If embedded, track page views
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, window.pymChild.parentUrl.split('?')[0]);
  }

  // Fetch your multi-series data
  d3.json('https://ix.cnn.io/data/stock-index-trends/latest_global_es.json').then((json) => {
    // Save the latest close display globally
    window.lastCloseDisplay = json.latest_close_display; // e.g., "March 12"

    rawData = json.data.map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      index: d.index,
      change: +d.change_pct,
    }));

    // Compute overall extents
    minDate = d3.min(rawData, d => d.date);
    maxDate = d3.max(rawData, d => d.date);
    minValue = d3.min(rawData, d => d.change);
    maxValue = d3.max(rawData, d => d.change);

    // For multi-series, we just keep everything
    chartData = rawData;

    // Build dek and timestamp
    const dek = json.summary_sentence;
    const lastUpdated = json.last_updated_str;
    const lastClosing = json.latest_close_date;

    d3.select('h2.dek').html(`${dek}`);
    d3.select('p.timestamp').text(`Nota: Las etiquetas se refieren a la ubicación del índice bursátil: el Hang Seng de Hong Kong; el DAX de Alemania; el STOXX 600 de Europa; el IPC México; y el S&P 500 de Estados Unidos. Los datos están actualizados hasta el último cierre del mercado: ${lastClosing}. Los valores de tendencia representan el cambio porcentual relativo desde el 21 de enero de 2025 hasta cada día. Los mercados estadounidenses estuvieron cerrados el Día de la Investidura, el 20 de enero de 2025.`);

    // Draw chart
    draw();
    window.onresize = draw;
  });
};

function draw() {
  // Container and sizing
  const container = d3.select('#graphic');
  container.html('');
  const width = container.node().clientWidth;
  const height = 400;
  const chartWidth = width - margins.left - margins.right;
  const chartHeight = height - margins.top - margins.bottom;

  // Mobile-friendly tick counts
  const isMobile = width <= 500;
  const xTicks = isMobile ? 3 : 4;
  const yTicks = isMobile ? 5 : 5;

  // Create scales
  const xScale = d3.scaleTime()
    .domain([minDate, maxDate])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0])
    .nice();

  // Axes
  const xAxis = d3.axisBottom(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) === '01') {
        return d3.timeFormat('%B %-d')(d);
      }
      return d3.timeFormat('%b %-d')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, ticks) => {
      const isTopTick = i === ticks.length - 1;
      if (isTopTick && d >= 1000) {
        return d3.format('.0%')(d);
      }
      return d3.format('.0%')(d);
    });

  // SVG
  const svg = container.append('svg')
    .attr('width', width)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(0, ${chartHeight})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const yGrid = d3.axisLeft(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat('');
  g.append('g')
    .attr('class', 'grid y')
    .call(yGrid);

  // Remove domain lines from the grids for a cleaner look
  g.selectAll('.grid.x .domain').remove();
  g.selectAll('.grid.y .domain').remove();

  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(0))
    .attr('y2', yScale(0))
    .attr('stroke', '#999')
    .attr('stroke-width', 1);

  // Multi-series line generator
  const line = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.change));

  // Group data by index using d3.nest (older d3)
  const series = d3.nest()
    .key(d => d.index)
    .entries(chartData);

  // Color scale
  const colorScale = d3.scaleOrdinal()
    .domain(series.map(s => s.key))
    .range(['#7c4ea5', '#53a796', '#f18851', '#5194c3', '#f8c153']);

  // Compute which index has the highest final change
  const topSeries = series.map((s) => {
    const sorted = s.values.sort((a, b) => a.date - b.date);
    return { key: s.key, lastChange: sorted[sorted.length - 1].change };
  });
  const topIndex = topSeries.reduce((acc, cur) => (cur.lastChange > acc.lastChange ? cur : acc), topSeries[0]).key;

  // Define custom Y offsets for specific indexes (optional)
  const offsetMapping = {
    Mexico: -6,
    Europe: 10,
  };

  series.forEach((s) => {
    const indexName = s.key;
    const values = s.values.sort((a, b) => a.date - b.date);
    g.append('path')
      .datum(values)
      .attr('fill', 'none')
      .attr('stroke', colorScale(indexName))
      .attr('stroke-width', 2)
      .attr('d', line);

    const lastPoint = values[values.length - 1];
    const lastX = xScale(lastPoint.date);
    const lastY = yScale(lastPoint.change);

    const anno = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${lastX},${lastY})`);

    anno.append('circle')
      .attr('r', 3)
      .attr('fill', colorScale(indexName));

    // If this index is the top performer, place the date in a separate text element above it
    if (indexName === topIndex && window.lastCloseDisplay) {
      anno.append('text')
        .attr('class', 'top-date')
        .attr('x', 6) // small horizontal offset
        .attr('y', -15) // negative so it appears above
        .style('font-size', '12px')
        .style('font-style', 'italic')
        .style('fill', '#262626')
        .text(window.lastCloseDisplay);
    }

    // Now the main label for the index name and its final percentage
    const customDy = offsetMapping[indexName] !== undefined
      ? offsetMapping[indexName]
      : 0;

    // A single text element with two tspans
    const label = anno.append('text')
      .attr('x', 6)
      .attr('y', customDy)
      .style('font-size', '12px')
      .style('fill', '#262626')
      .attr('class', 'last-point');

    label.append('tspan')
      .style('font-weight', 'bold')
      .text(`${indexName}: `);

    label.append('tspan')
      .text(`${(lastPoint.change * 100).toFixed(1)}%`);
  });

  window.pymChild.sendHeight();
}
